import { Box, Text } from "@saleor/macaw-ui-next";
import React from "react";

import { InventoryAnalyticsData } from "../../types";

interface InventoryInsightsProps {
  data: InventoryAnalyticsData;
  currency: string;
}

export const InventoryInsights: React.FC<InventoryInsightsProps> = ({ data, currency }) => {
  const formatCurrency = (val: number) =>
    `${currency} ${Number(val || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const cards = [
    { label: "Total Inventory Value", value: formatCurrency(data.totalInventoryValue) },
    { label: "Total Stock on Hand", value: `${data.totalStockUnits.toLocaleString()} units` },
    { label: "Allocated to Orders", value: `${data.totalAllocatedUnits.toLocaleString()} units` },
    { label: "Sell-Through Rate", value: `${data.sellThroughRatePercentage}%` },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={6}>
      {/* Metric Cards */}
      <Box display="grid" __gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))" gap={4}>
        {cards.map((card, idx) => (
          <Box
            key={idx}
            padding={4}
            borderWidth={1}
            borderStyle="solid"
            borderColor="default1"
            borderRadius={3}
            backgroundColor="default1"
            display="flex"
            flexDirection="column"
            gap={1}
          >
            <Text size={2} color="default1" fontWeight="bold">
              {card.label}
            </Text>
            <Text size={6} fontWeight="bold">
              {card.value}
            </Text>
          </Box>
        ))}
      </Box>

      {/* Low-Stock Alerts Section */}
      <Box
        borderWidth={1}
        borderStyle="solid"
        borderColor="default1"
        borderRadius={3}
        backgroundColor="default1"
        padding={4}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text size={4} fontWeight="bold" color="critical1">
            ⚠️ Low-Stock Alerts (Below Threshold)
          </Text>
          <Text size={2} color="default1">
            {data.lowStockAlerts.length} Alerts
          </Text>
        </Box>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fafafa" }}>
                <th style={{ padding: "10px" }}>Product</th>
                <th style={{ padding: "10px" }}>Variant</th>
                <th style={{ padding: "10px", textAlign: "center" }}>Stock Level</th>
                <th style={{ padding: "10px", textAlign: "center" }}>Allocated</th>
                <th style={{ padding: "10px" }}>Warehouse</th>
              </tr>
            </thead>
            <tbody>
              {data.lowStockAlerts.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#2e7d32",
                      fontWeight: "bold",
                    }}
                  >
                    ✓ All inventory levels are healthy.
                  </td>
                </tr>
              ) : (
                data.lowStockAlerts.map((it, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "10px", fontWeight: "bold" }}>{it.productName}</td>
                    <td style={{ padding: "10px", color: "#666" }}>
                      {it.variantName || "Default"}
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        textAlign: "center",
                        fontWeight: "bold",
                        color: it.stockQuantity <= 5 ? "#c62828" : "#e65100",
                      }}
                    >
                      {it.stockQuantity}
                    </td>
                    <td style={{ padding: "10px", textAlign: "center" }}>{it.allocatedQuantity}</td>
                    <td style={{ padding: "10px", color: "#555" }}>{it.warehouseName}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Box>

      {/* Stock Velocity Movement (Fast vs Slow Movers) */}
      <Box display="grid" __gridTemplateColumns="repeat(auto-fit, minmax(320px, 1fr))" gap={4}>
        {/* Fast Moving */}
        <Box
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          borderRadius={3}
          backgroundColor="default1"
          padding={4}
          display="flex"
          flexDirection="column"
          gap={3}
        >
          <Text size={4} fontWeight="bold">
            🚀 Fast-Moving Inventory
          </Text>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fafafa" }}>
                  <th style={{ padding: "8px", textAlign: "left" }}>Product</th>
                  <th style={{ padding: "8px", textAlign: "right" }}>Sold</th>
                </tr>
              </thead>
              <tbody>
                {data.fastMoving.length === 0 ? (
                  <tr>
                    <td colSpan={2} style={{ padding: "12px", textAlign: "center", color: "#888" }}>
                      No fast movement data.
                    </td>
                  </tr>
                ) : (
                  data.fastMoving.map((fm, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f5f5f5" }}>
                      <td style={{ padding: "8px", textAlign: "left" }}>{fm.productName}</td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "right",
                          fontWeight: "bold",
                          color: "#2e7d32",
                        }}
                      >
                        {fm.quantitySold.toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Box>

        {/* Slow Moving */}
        <Box
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          borderRadius={3}
          backgroundColor="default1"
          padding={4}
          display="flex"
          flexDirection="column"
          gap={3}
        >
          <Text size={4} fontWeight="bold" color="critical1">
            🐢 Slow-Moving Inventory
          </Text>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #eee", backgroundColor: "#fafafa" }}>
                  <th style={{ padding: "8px", textAlign: "left" }}>Product</th>
                  <th style={{ padding: "8px", textAlign: "right" }}>Sold</th>
                </tr>
              </thead>
              <tbody>
                {data.slowMoving.length === 0 ? (
                  <tr>
                    <td colSpan={2} style={{ padding: "12px", textAlign: "center", color: "#888" }}>
                      No slow movement data.
                    </td>
                  </tr>
                ) : (
                  data.slowMoving.map((sm, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f5f5f5" }}>
                      <td style={{ padding: "8px", textAlign: "left" }}>{sm.productName}</td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "right",
                          fontWeight: "bold",
                          color: "#888",
                        }}
                      >
                        {sm.quantitySold.toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Box>
      </Box>
    </Box>
  );
};
